import React from "react";
import {
  I2cAccordion,
  I2cIconButton,
} from "@webcomponents/i2cwebcomponents/dist/react";
import ProjectInformationOverview from "./ProjectInformationOverview";
import ProgramInformationOverview from "./ProgramInformationOverview";
import CardNetworkAndBinInformationOverview from "./CardNetworkAndBinInformationOverview";
import InterfacesAndComponentsOverview from "./InterfacesAndComponentsOverview";
import OptionalFeatures from "./OptionalFeaturesOverview";
import ConnectivityAndCommunicationLinkOverview from "./ConnectivityAndCommunicationLinkOverview";
import WebServicesAPIOverview from "./WebServicesAPIOverview";
import CooperativeAuthenticationOverview from "./CooperativeAuthenticationOverview";
import SettlementAndReconciliationOverview from "./SettlementAndReconciliationOverview";
import AdditionalCommentsOverview from "./AdditionalCommentsOverview";
import UseCaseDocumentOverView from "./UseCaseDocumentOverView";
import SignoffOverview from "./SignoffOverview";
import I2CStakeholderSignOffOverview from "./I2CStakeholderSignOffOverview";
import PrepaidProgramFeatures from "./ProgramFeaturesOverview";
import GeneralInformationCreditOverview from "./ProgramFeaturesCredit/GeneralInformationCreditOverview";
import { UserStateContext } from "../../../Context/AuthContext";

const ReviewAccordionRenderer = ({
  formData = {},
  signatureFormData = {},
  i2cStakeHolderSignOff = {},
  type = "debit",
  editable = false,
  onEdit = () => {},
  showClientSignoff = true,
  clientUser = false,
  accordionSections = [],
  variant = "borderless",
}) => {
  // Default accordion sections if not provided
  const defaultSections = [
    { sectionId: "0", summary: "Project & Client Information" },
    { sectionId: "1", summary: "Program Information" },
    { sectionId: "2", summary: "Card Network And Bin Info" },
    { sectionId: "3", summary: "Interfaces and Components" },
    { sectionId: "4", summary: "Value Added Services" },
    { sectionId: "5", summary: "Connectivity & File Exchange" },
    { sectionId: "6", summary: "Web Services API" },
    { sectionId: "7", summary: "Cooperative Authentication" },
    { sectionId: "8", summary: "Settlement & Reconciliation" },
    { sectionId: "9", summary: "Additional Comments" },
    { sectionId: "10", summary: "Use Case Document" },
    { sectionId: "11", summary: "Client Signoff" },
    { sectionId: "12", summary: "I2C Internal Stakeholder Review & Sign Off" },
  ];

  const appContext = UserStateContext();
  const roleName = String(appContext.roleName || "").toLowerCase();
  const roleId = String(appContext.userRole || "");

  const css = `
  .accordion-container{
    margin-bottom:0px;
  }

  .accordion-container::part(base) {
    border: 0;
  }

  .accordion-container::part(summary) {
    font-family: Inter;
    font-size: var(--i2c-font-size-text-lg);
    font-weight: 700;
    line-height: var(--i2c-line-height-1-5);
  }
  
  .accordion-container::part(header) {
    border-bottom: 1px solid var(--border-secondary);
    padding:var(--i2c-spacing-2) 0px var(--i2c-spacing-2) 0px; 
    margin-bottom: 0;
  }

  .accordion-container::part(icon-container) {
    margin-top: 12px;
  }

  .accordion-container[open]::part(header) {
    border-bottom: 0;
    margin-bottom: 0;
  }

  .accordion-container::part(content) {
    border-bottom: 1px solid var(--border-secondary);    
  }

  .border-less-container::part(header){
    padding:var(--i2c-spacing-6) 0;
    border-top: 0;
  }
`;

  const sections =
    accordionSections.length > 0 ? accordionSections : defaultSections;

  // Determine accordion style based on variant
  const getAccordionStyle = () => {
    if (variant === "borderless") {
      return {
        width: "100%",
        "--accordion-bg": "var(--bg-primary_alt)",
      };
    }
    return {
      width: "100%",
      "--accordion-bg": "var(--bg-primary)",
    };
  };

  const renderEditButton = (sectionIndex, innerIndex = 0) => {
    if (!editable) return null;
    
    // Hide edit button for stakeholder users (userRole 7)
    if (roleId === "7" && roleName === "stakeholder") {
      return null;
    }

    return (
      <span slot="suffix">
        <I2cIconButton
          onClick={() => onEdit(sectionIndex, innerIndex)}
          name="linear-edit-2"
          label="Edit"
          style={{ fontSize: "var(--i2c-font-size-text-md)", marginTop: "8px" }}
        />
      </span>
    );
  };

  return (
    <>
    <style>{css}</style>
      {sections.map((section) => {
        const sectionId = section.sectionId;

        // Section 0: Project & Client Information
        if (sectionId === "0") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <ProjectInformationOverview type={type} data={formData["0"]} />
              {renderEditButton(0, 0)}
            </I2cAccordion>
          );
        }

        // Section 1: Program Information
        if (sectionId === "1") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <div>
                <ProgramInformationOverview type={type} data={formData["1"]} />
                <div className="mb-16"></div>
                {type === "credit" ? (
                  <GeneralInformationCreditOverview data={formData["1"]} />
                ) : (
                  <PrepaidProgramFeatures data={formData["1"]} />
                )}
              </div>
              {renderEditButton(1, 0)}
            </I2cAccordion>
          );
        }

        // Section 2: Card Network And Bin Info
        if (sectionId === "2") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <CardNetworkAndBinInformationOverview data={formData["2"]} />
              {renderEditButton(2, 0)}
            </I2cAccordion>
          );
        }

        // Section 3: Interfaces and Components
        if (sectionId === "3") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <InterfacesAndComponentsOverview data={formData["3"]} />
              {renderEditButton(3, 0)}
            </I2cAccordion>
          );
        }

        // Section 4: Value Added Services / Optional Features
        if (sectionId === "4") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <OptionalFeatures data={formData["4"]} />
              {renderEditButton(4, 0)}
            </I2cAccordion>
          );
        }

        // Section 5: Connectivity & File Exchange
        if (sectionId === "5") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <ConnectivityAndCommunicationLinkOverview data={formData["5"]} />
              {renderEditButton(5, 0)}
            </I2cAccordion>
          );
        }

        // Section 6: Web Services API
        if (sectionId === "6") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <WebServicesAPIOverview data={formData["6"]} />
              {renderEditButton(6, 0)}
            </I2cAccordion>
          );
        }

        // Section 7: Cooperative Authentication
        if (sectionId === "7") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <CooperativeAuthenticationOverview data={formData["7"]} />
              {renderEditButton(7, 0)}
            </I2cAccordion>
          );
        }

        // Section 8: Settlement & Reconciliation
        if (sectionId === "8") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <SettlementAndReconciliationOverview data={formData["8"]} />
              {renderEditButton(8, 0)}
            </I2cAccordion>
          );
        }

        // Section 9: Additional Comments
        if (sectionId === "9") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <AdditionalCommentsOverview data={formData["9"]} />
              {renderEditButton(9, 0)}
            </I2cAccordion>
          );
        }

        // Section 10: Use Case Document
        if (sectionId === "10") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <UseCaseDocumentOverView data={formData["10"]} />
              {renderEditButton(10, 0)}
            </I2cAccordion>
          );
        }

        // Section 11: Client Signoff
        if (sectionId === "11") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <SignoffOverview data={signatureFormData} />
              {clientUser && renderEditButton(11, 1)}
            </I2cAccordion>
          );
        }

        // Section 12: I2C Internal Stakeholder Review & Sign Off
        if (sectionId === "12") {
          return (
            <I2cAccordion
              key={sectionId}
              data-review-section={sectionId}
              iconStyle="custom"
              iconClose="cs-linear-chevron-down"
              iconOpen="cs-linear-chevron-up"
              summary={section.summary}
              variant={variant}
              className="accordion-container border-less-container"
              style={getAccordionStyle()}
            >
              <I2CStakeholderSignOffOverview data={i2cStakeHolderSignOff} />
              {roleName === "stakeholder" && renderEditButton(12, 0)}
            </I2cAccordion>
          );
        }

        return null;
      })}
    </>
  );
};

export default ReviewAccordionRenderer;
